import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMealApi } from '../../Hooks/useMealApi';
import { useSnackbar } from '../stateless/SnackBarProvider';
// import { MealDataType } from '../statefull/ManageMeal/types';

/**
 * Query parameters type for meal queries
 */
type MealQueryParams = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
  storeTypeFilter?: string;
  merchantId?: string;
};

/**
 * Hook to fetch all meals with pagination and filters
 */
export const useAllMeals = (queryParams: MealQueryParams) => {
  const { getAllMeals } = useMealApi();

  const serializedParams = JSON.stringify(queryParams);

  const {
    data,
    isLoading: isFetching,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['meals', serializedParams],
    queryFn: () => {
      return getAllMeals({
        ...queryParams,
      });
    },
    enabled: Boolean(serializedParams),
    retry: false,
  });

  return {
    data,
    isLoading: isFetching || isRefetching,
    refetch,
  };
};

/**
 * Hook to create a new meal
 */
export const useCreateMeal = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { createMeal } = useMealApi();

  return useMutation({
    mutationKey: ['CreateMeal'],
    mutationFn: ({ modifiedValues }: { modifiedValues: any }) => {
      return createMeal(modifiedValues);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Meal created successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['meals'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error?.response?.data?.message || 'Failed to create meal',
        'error'
      );
    },
  });
};

/**
 * Hook to edit an existing meal
 */
export const useEditMeal = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editMeal } = useMealApi();

  return useMutation({
    mutationKey: ['EditMeal'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editMeal(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Meal updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['meals'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['meal-detail'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error?.response?.data?.message || 'Failed to update meal',
        'error'
      );
    },
  });
};

/**
 * Hook to toggle meal status (block/unblock)
 */
export const usePatchMeal = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { getToggleStatus } = useMealApi();

  return useMutation({
    mutationKey: ['PatchMeal'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      getToggleStatus({ id, status: data.status }),
    onSuccess: (_response, variables) => {
      const action =
        variables.data.status === 'unblocked' ? 'unblocked' : 'blocked';
      showMessage(`Meal ${action} successfully`, 'success');
      queryClient.invalidateQueries({
        queryKey: ['meals'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error?.response?.data?.detail?.[0]?.type || 'Failed to update meal status',
        'error'
      );
    },
  });
};

/**
 * Hook to delete meal(s)
 */
export const useDeleteMeal = () => {
  const queryClient = useQueryClient();
  const { deleteMeal } = useMealApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteMeal'],
    mutationFn: (ids: string[]) => {
      return deleteMeal(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Meal deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['meals'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error?.response?.data?.message || 'Failed to delete meal',
        'error'
      );
    },
  });
};

/**
 * Hook to fetch a single meal by ID
 */
export const useMealById = (id: string) => {
  const { getMealById } = useMealApi();

  const {
    data,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['meal-detail', id],
    queryFn: () => getMealById(id),
    enabled: Boolean(id),
    retry: false,
  });

  return {
    data,
    isLoading: isLoading || isRefetching,
    refetch,
  };
};
